import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiErrorDetail, triggerFileDownload } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Scales, FloppyDisk, ArrowsClockwise, Warning, FilePdf, FileXls } from "@phosphor-icons/react";

/**
 * RABillComparisonSheet — side-by-side viewer for "Original BOQ ↔ This RA Bill"
 *
 * Shows per-line contract qty · rate · BOQ amount · previous bill · this bill (editable)
 * · cumulative bill · cumulative % · variance.
 *
 * Inline-editable — the user tweaks Supply/Install/T&C this-bill qty and saves via
 * PUT /ra-bills/{id} (same payload as RABillSheet). A "Refresh" re-fetches the
 * comparison from the backend so cumulative/variance stays authoritative.
 *
 * Props:
 *   • raId — RA bill id to compare
 *   • onClose — dismiss
 *   • onSaved — after successful save (used by RABilling to refresh lists)
 */
export default function RABillComparisonSheet({ raId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Edits stored per-boq-item-index → {supply_this_qty, install_this_qty, tnc_this_qty}
  const [edits, setEdits] = useState({});
  const [ra, setRa] = useState(null);
  const [sectionFilter, setSectionFilter] = useState("");
  const [onlyBilled, setOnlyBilled] = useState(false);

  const load = useCallback(async () => {
    if (!raId) return;
    setLoading(true);
    try {
      const [{ data: cmp }, { data: raDoc }] = await Promise.all([
        api.get(`/ra-bills/${raId}/comparison`),
        api.get(`/ra-bills/${raId}`),
      ]);
      setData(cmp);
      setRa(raDoc);
      setEdits({});
      setDirty(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [raId]);

  useEffect(() => { load(); }, [load]);

  const patchLine = (idx, key, value) => {
    setEdits((e) => ({
      ...e,
      [idx]: {
        ...(e[idx] || {}),
        [key]: Number(value) || 0,
      },
    }));
    setDirty(true);
  };

  const editedItems = useMemo(() => {
    if (!data) return [];
    return data.rows.map((r) => {
      const ed = edits[r.boq_item_index] || {};
      return {
        boq_item_index: r.boq_item_index,
        supply_this_qty: ed.supply_this_qty ?? r.supply_this_qty,
        install_this_qty: ed.install_this_qty ?? r.install_this_qty,
        tnc_this_qty: ed.tnc_this_qty ?? r.tnc_this_qty,
        supply_pct: r.supply_pct,
        install_pct: r.install_pct,
        tnc_pct: r.tnc_pct,
      };
    });
  }, [data, edits]);

  const save = async () => {
    if (!ra) return;
    setBusy(true);
    try {
      const payload = {
        boq_id: ra.boq_id,
        bill_period_start: ra.bill_period_start,
        bill_period_end: ra.bill_period_end,
        bill_date: ra.bill_date,
        bill_no_client: ra.bill_no_client,
        is_final_bill: ra.is_final_bill,
        is_interstate: ra.is_interstate,
        tds_pct: ra.tds_pct, retention_pct: ra.retention_pct,
        car_policy_amount: ra.car_policy_amount || 0,
        hold_quality: ra.hold_quality || 0,
        hold_other: ra.hold_other || 0,
        less_secured_advance: ra.less_secured_advance || 0,
        less_advance: ra.less_advance || 0,
        hold_other_release: ra.hold_other_release || 0,
        already_paid: ra.already_paid || 0,
        prepared_by: ra.prepared_by || "",
        verified_by: ra.verified_by || "",
        approved_by: ra.approved_by || "",
        notes: ra.notes || "",
        measurement_sheet_ids: ra.measurement_sheet_ids || [],
        client_challan_ids: ra.client_challan_ids || [],
        items: editedItems,
      };
      await api.put(`/ra-bills/${raId}`, payload);
      toast.success("RA bill updated");
      onSaved?.();
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!raId) return null;

  const sections = Array.from(new Set((data?.rows || []).map((r) => r.section).filter(Boolean))).sort();
  const filteredRows = (() => {
    const rows = data?.rows || [];
    return rows.filter((r) => {
      if (sectionFilter && r.section !== sectionFilter) return false;
      const ed = edits[r.boq_item_index] || {};
      const anyThisBillQty = (ed.supply_this_qty ?? r.supply_this_qty) + (ed.install_this_qty ?? r.install_this_qty) + (ed.tnc_this_qty ?? r.tnc_this_qty);
      if (onlyBilled && anyThisBillQty === 0 && !r.this_qty && !r.cum_qty) return false;
      return true;
    });
  })();

  return (
    <Sheet open onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent
        className="w-[min(1500px,100vw)] sm:max-w-none overflow-y-auto"
        data-testid="ra-comparison-sheet"
      >
        <SheetHeader className="mb-3">
          <SheetTitle className="font-display flex items-center gap-2 flex-wrap">
            <Scales size={18} className="text-[#EA580C]" />
            Compare · Original BOQ ↔ {data?.ra_bill_no || "RA Bill"}
            {data && (
              <span className="text-[11.5px] text-slate-500 font-normal">
                · {data.boq_no} · {data.boq_title} · {data.client_name}
              </span>
            )}
            <Button
              size="sm"
              variant="outline"
              className="ml-auto h-8 rounded-sm text-[11.5px]"
              onClick={load}
              data-testid="cmp-refresh-btn"
            >
              <ArrowsClockwise size={12} className="mr-1" /> Refresh
            </Button>
          </SheetTitle>
        </SheetHeader>

        {loading || !data ? (
          <div className="text-center text-[12.5px] text-slate-400 py-12">Loading comparison…</div>
        ) : (
          <div className="space-y-3">
            {/* Totals band */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="cmp-totals">
              <TotalTile label="Original BOQ ₹" value={fmtMoney(data.totals.boq_amount)} tone="slate" />
              <TotalTile label="This Bill ₹" value={fmtMoney(data.totals.this_bill_amount)} tone="orange" />
              <TotalTile label="Cumulative ₹" value={fmtMoney(data.totals.cumulative_amount)} tone="dark" />
              <TotalTile label="Cumulative %" value={`${(data.totals.cumulative_pct || 0).toFixed(2)}%`} tone="emerald" />
              <TotalTile label="Balance BOQ ₹" value={fmtMoney(data.totals.boq_amount - data.totals.cumulative_amount)} tone="slate" />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 text-[11.5px]">
              <div className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">Filter:</div>
              <select
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value)}
                className="border border-slate-200 rounded-sm h-7 text-[11.5px] px-2"
                data-testid="cmp-section-filter"
              >
                <option value="">All sections</option>
                {sections.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <label className="flex items-center gap-1.5 cursor-pointer" data-testid="cmp-only-billed">
                <input type="checkbox" checked={onlyBilled} onChange={(e) => setOnlyBilled(e.target.checked)} className="rounded-sm" />
                <span>Only billed lines</span>
              </label>
              {dirty && <span className="text-amber-700 font-semibold flex items-center gap-1"><Warning size={12} /> Unsaved changes</span>}
            </div>

            {/* Comparison Table */}
            <div className="border border-slate-200 rounded-sm overflow-x-auto">
              <Table className="bc-list text-[11px]">
                <TableHeader>
                  <TableRow className="bg-[#0F172A]">
                    <TableHead className="text-white/90 w-12">SR</TableHead>
                    <TableHead className="text-white/90 w-60">Description</TableHead>
                    <TableHead className="text-white/90 w-16">Unit</TableHead>
                    <TableHead className="text-white/90 text-right w-16" title="Original BOQ Qty">BOQ Qty</TableHead>
                    <TableHead className="text-white/90 text-right w-20">Rate</TableHead>
                    <TableHead className="text-white/90 text-right w-24 bg-[#0F172A]/80" title="Original BOQ Amount">BOQ ₹</TableHead>
                    <TableHead className="text-white/90 text-right w-20 bg-[#EA580C]/80" title="Previous cumulative">Prev</TableHead>
                    <TableHead className="text-white/90 text-right w-20 bg-[#EA580C]/80" title="Supply this bill (editable)">Supply</TableHead>
                    <TableHead className="text-white/90 text-right w-20 bg-[#EA580C]/80" title="Install this bill (editable)">Install</TableHead>
                    <TableHead className="text-white/90 text-right w-20 bg-[#EA580C]/80" title="T&C this bill (editable)">T&C</TableHead>
                    <TableHead className="text-white/90 text-right w-24 bg-[#EA580C]/80">This ₹</TableHead>
                    <TableHead className="text-white/90 text-right w-24">Cum ₹</TableHead>
                    <TableHead className="text-white/90 text-right w-16">Cum %</TableHead>
                    <TableHead className="text-white/90 text-right w-24">Balance ₹</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const rows = [];
                    let lastSection = "";
                    filteredRows.forEach((r) => {
                      if (r.section && r.section !== lastSection) {
                        lastSection = r.section;
                        rows.push(
                          <TableRow key={`sec-${r.boq_item_index}`} className="bg-slate-100">
                            <TableCell colSpan={14} className="py-1.5 text-[10.5px] font-semibold uppercase tracking-widest text-slate-700">
                              <span className="text-[#EA580C] mr-2">§</span> {r.section}
                            </TableCell>
                          </TableRow>
                        );
                      }
                      const ed = edits[r.boq_item_index] || {};
                      const supplyQ = ed.supply_this_qty ?? r.supply_this_qty;
                      const installQ = ed.install_this_qty ?? r.install_this_qty;
                      const tncQ = ed.tnc_this_qty ?? r.tnc_this_qty;
                      // Estimated this-bill amount for the edited row (feedback while typing)
                      const estThisAmt =
                        supplyQ * (r.rate * (r.supply_pct || 0) / 100) +
                        installQ * (r.rate * (r.install_pct || 0) / 100) +
                        tncQ * (r.rate * (r.tnc_pct || 0) / 100);
                      const cumAmt = (r.prev_amt || 0) + estThisAmt;
                      const balance = r.boq_amount - cumAmt;
                      const cumPct = r.boq_amount ? (cumAmt / r.boq_amount) * 100 : 0;
                      const isEdited = edits[r.boq_item_index] != null;
                      const exceeds = r.qty_exceeds_boq || cumPct > 100;
                      rows.push(
                        <TableRow key={`row-${r.boq_item_index}`} className={isEdited ? "bg-amber-50" : ""} data-testid={`cmp-row-${r.boq_item_index}`}>
                          <TableCell className="font-mono">{r.sr_no}</TableCell>
                          <TableCell className="text-[11px] text-slate-800">{r.description}</TableCell>
                          <TableCell className="uppercase">{r.unit}</TableCell>
                          <TableCell className="text-right font-mono-num">{r.contract_qty}</TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(r.rate)}</TableCell>
                          <TableCell className="text-right font-mono-num font-semibold text-slate-900">{fmtMoney(r.boq_amount)}</TableCell>
                          <TableCell className="text-right font-mono-num text-slate-500">{r.prev_qty || ""}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={supplyQ || 0}
                              onChange={(e) => patchLine(r.boq_item_index, "supply_this_qty", e.target.value)}
                              className="h-6 text-right font-mono-num text-[11px] w-20 px-1"
                              data-testid={`cmp-supply-${r.boq_item_index}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={installQ || 0}
                              onChange={(e) => patchLine(r.boq_item_index, "install_this_qty", e.target.value)}
                              className="h-6 text-right font-mono-num text-[11px] w-20 px-1"
                              data-testid={`cmp-install-${r.boq_item_index}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={tncQ || 0}
                              onChange={(e) => patchLine(r.boq_item_index, "tnc_this_qty", e.target.value)}
                              className="h-6 text-right font-mono-num text-[11px] w-20 px-1"
                              data-testid={`cmp-tnc-${r.boq_item_index}`}
                            />
                          </TableCell>
                          <TableCell className="text-right font-mono-num font-semibold text-[#EA580C]">{fmtMoney(estThisAmt)}</TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(cumAmt)}</TableCell>
                          <TableCell className={`text-right font-mono-num ${exceeds ? "text-red-600 font-bold" : cumPct >= 100 ? "text-emerald-700 font-bold" : cumPct > 0 ? "text-slate-800" : "text-slate-400"}`}>
                            {cumPct.toFixed(1)}%
                          </TableCell>
                          <TableCell className={`text-right font-mono-num ${balance < 0 ? "text-red-600" : "text-slate-700"}`}>{fmtMoney(balance)}</TableCell>
                        </TableRow>
                      );
                    });
                    return rows;
                  })()}
                </TableBody>
              </Table>
              {filteredRows.length === 0 && (
                <div className="text-center text-[12px] text-slate-400 p-6">
                  No lines match the current filter.
                </div>
              )}
            </div>

            <div className="text-[10.5px] text-slate-500 italic">
              Tip: rows highlighted amber have unsaved changes · orange columns show what this RA bill contributes ·
              % over 100 or negative balance means cumulative exceeds the BOQ (change-order territory).
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-3 mt-4 border-t border-slate-200 sticky bottom-0 bg-white z-10">
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="cmp-close">Close</Button>
          {data && (
            <>
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={() => triggerFileDownload(`/boqs/${data.boq_id}/export-client-format?ra_bill_id=${data.ra_bill_id}`, `ClientFormat-${data.ra_bill_no}.xlsx`)}
                data-testid="cmp-export-xlsx"
                title="Download in client's original Excel template"
              >
                <FileXls size={13} className="mr-1" /> Client Format Excel
              </Button>
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={() => triggerFileDownload(`/boqs/${data.boq_id}/client-format-pdf?ra_bill_id=${data.ra_bill_id}`, `ClientFormat-${data.ra_bill_no}.pdf`)}
                data-testid="cmp-export-pdf"
                title="Download as client-ready PDF"
              >
                <FilePdf size={13} className="mr-1" /> Client Format PDF
              </Button>
            </>
          )}
          <Button
            className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white ml-auto"
            onClick={save}
            disabled={busy || !dirty}
            data-testid="cmp-save"
          >
            <FloppyDisk size={13} className="mr-1" />
            {busy ? "Saving…" : dirty ? "Save Changes" : "No Changes"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TotalTile({ label, value, tone }) {
  const cls = {
    slate: "bg-white border-slate-200 text-slate-900",
    orange: "bg-[#EA580C] text-white border-[#EA580C]",
    dark: "bg-[#0F172A] text-white border-[#0F172A]",
    emerald: "bg-emerald-600 text-white border-emerald-600",
  }[tone] || "bg-white border-slate-200 text-slate-900";
  return (
    <div className={`border ${cls} rounded-sm px-3 py-2`}>
      <div className={`text-[9.5px] uppercase tracking-[0.14em] font-semibold ${tone === "slate" ? "text-slate-500" : "text-white/70"}`}>{label}</div>
      <div className="text-[15px] font-bold font-mono-num mt-0.5">{value}</div>
    </div>
  );
}
